import { z } from 'zod';
import { E164_PHONE_RE } from './notification-subscription';
import { CommunityTypeSchema } from './community';

/**
 * #547 RESIDENT SELF-OPT-IN — the public /subscribe contract (double-opt-in PIN flow).
 *
 * A resident opts IN to a community's EMERGENCY NOTIFICATIONS on a public page and confirms
 * ownership of the number via a texted one-time PIN. This module is the shared boundary
 * between apps/web (the /subscribe page) and apps/api (the /api/public/subscribe/* routes):
 * the request schemas, the forgiving-input→E.164 normalizer, and the double-opt-in tunables.
 *
 * NOUN DISCIPLINE (R3): residents subscribe to "emergency notifications from {community}" —
 * never "alerts" (that reads as the reserved Alert Ready broadcast) and never "public
 * information releases" (operator jargon, §2.2). The platform is the pipe; the community issues.
 */

/** The one-time PIN is valid for 15 minutes from pin_sent_at. */
export const RESIDENT_PIN_EXPIRY_MINUTES = 15;
/** Wrong-PIN attempts allowed before the resident must request a fresh PIN. */
export const RESIDENT_PIN_MAX_ATTEMPTS = 5;
/** A resident cannot re-request a PIN within this many seconds of the last send (abuse throttle). */
export const RESIDENT_PIN_RESEND_COOLDOWN_SECONDS = 60;

/**
 * Normalize a human-typed phone number to E.164, forgiving common Canadian formats:
 * "204-555-1234", "(204) 555-1234", "204 555 1234", "12045551234", "+12045551234". Returns the
 * E.164 string (e.g. "+12045551234") or `null` when the input is not an unambiguous NANP number.
 *
 * Rules: a leading "+" is treated as already-international (validated as-is); a bare 10-digit
 * number is assumed NANP and prefixed "+1"; an 11-digit number starting with "1" gets a "+";
 * anything else is rejected (never guess a country code). The result is re-validated against
 * the same E164_PHONE_RE the #6a operator flow uses, so a resident number and an operator
 * number are byte-comparable.
 */
export function normalizeCanadianPhoneToE164(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;
  const digits = trimmed.replace(/\D/g, '');
  let e164: string;
  if (trimmed.startsWith('+')) {
    e164 = `+${digits}`;
  } else if (digits.length === 10) {
    e164 = `+1${digits}`;
  } else if (digits.length === 11 && digits.startsWith('1')) {
    e164 = `+${digits}`;
  } else {
    return null;
  }
  return E164_PHONE_RE.test(e164) ? e164 : null;
}

/** A raw human-typed phone that server-side normalization must accept (not the strict E.164 gate). */
const RawPhoneSchema = z.string().trim().min(7).max(32);

/** POST /api/public/subscribe/request-pin — start (or resend) a pending self-subscribe. */
export const ResidentRequestPinSchema = z.object({
  community_id: z.string().uuid(),
  phone: RawPhoneSchema,
});
export type ResidentRequestPin = z.infer<typeof ResidentRequestPinSchema>;

/** POST /api/public/subscribe/confirm-pin — confirm the pending row with the texted PIN. */
export const ResidentConfirmPinSchema = z.object({
  community_id: z.string().uuid(),
  phone: RawPhoneSchema,
  pin: z.string().trim().regex(/^\d{6}$/, 'the confirmation code is 6 digits'),
});
export type ResidentConfirmPin = z.infer<typeof ResidentConfirmPinSchema>;

/**
 * POST /api/public/subscribe/unsubscribe — by unsubscribe_token (preferred, one-click) OR by
 * community_id + phone. `.refine` requires exactly one identification mode is present.
 */
export const ResidentUnsubscribeSchema = z
  .object({
    unsubscribe_token: z.string().trim().min(16).max(128).optional(),
    community_id: z.string().uuid().optional(),
    phone: RawPhoneSchema.optional(),
  })
  .refine((v) => !!v.unsubscribe_token || (!!v.community_id && !!v.phone), {
    message: 'provide unsubscribe_token, or both community_id and phone',
  });
export type ResidentUnsubscribe = z.infer<typeof ResidentUnsubscribeSchema>;

/** GET /api/public/communities response element — the minimum a resident needs to pick. */
export const PublicCommunitySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  community_type: CommunityTypeSchema,
});
export type PublicCommunity = z.infer<typeof PublicCommunitySchema>;
